import { useCallback, useEffect, useState } from 'react';
import {
  CheckCircle2, ClipboardList, Download, FileSpreadsheet, FileText, Loader2, Pencil, Play, Radio, Search, ShieldCheck, Square, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { batchApi, SIGNATURE_MEANINGS, type Batch, type BatchField, type BatchTemplate } from '../../utils/batches';

interface BatchReportsProps {
  clientId: string;
  token: string;
  userRole?: string;
}

const POLL_MS = 5000;
const SIGNER_ROLES = ['admin', 'supervisor', 'client_admin', 'super_admin'];
const selectCls = 'h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';

const formatDuration = (ms: number) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m ${String(sec).padStart(2, '0')}s`;
};
const formatTime = (iso?: string | null) => (iso ? new Date(iso).toLocaleString() : '-');
const toLocalInput = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

function StatusBadge({ status }: { status: Batch['status'] }) {
  const meta = {
    running: 'border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400',
    completed: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    aborted: 'border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400',
  }[status];
  return <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${meta}`}>{status === 'running' ? 'Running' : status === 'completed' ? 'Completed' : 'Aborted'}</span>;
}

function FieldInputs({ fields, values, onChange }: { fields: BatchField[]; values: Record<string, string>; onChange: (v: Record<string, string>) => void }) {
  return (
    <>
      {fields.map((f) => (
        <div key={f.label} className="space-y-1.5">
          <Label>{f.label}{f.required ? ' *' : ''}</Label>
          <Input value={values[f.label] || ''} maxLength={120} onChange={(e) => onChange({ ...values, [f.label]: e.target.value })} />
        </div>
      ))}
    </>
  );
}

export function BatchReports({ clientId, token, userRole = '' }: BatchReportsProps) {
  const [templates, setTemplates] = useState<BatchTemplate[]>([]);
  const [running, setRunning] = useState<Batch[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tab, setTab] = useState<'batches' | 'process'>('batches');
  const [query, setQuery] = useState('');
  const [, setTick] = useState(0);
  const [busy, setBusy] = useState(false);

  const [starting, setStarting] = useState<BatchTemplate | null>(null);
  const [startForm, setStartForm] = useState({ batchNumber: '', product: '', fields: {} as Record<string, string> });
  const [stopping, setStopping] = useState<Batch | null>(null);
  const [stopForm, setStopForm] = useState({ status: 'completed' as 'completed' | 'aborted', notes: '' });
  const [editing, setEditing] = useState<Batch | null>(null);
  const [editForm, setEditForm] = useState({ product: '', fields: {} as Record<string, string> });
  const [signing, setSigning] = useState<Batch | null>(null);
  const [signForm, setSignForm] = useState({ meaning: 'Review', reason: '', password: '' });

  const [range, setRange] = useState(() => ({ from: toLocalInput(new Date(Date.now() - 24 * 3600 * 1000)), to: toLocalInput(new Date()) }));

  const load = useCallback(async () => {
    try {
      const data = await batchApi.overview(token, clientId, query.trim());
      setTemplates(data.templates);
      setRunning(data.running);
      setBatches(data.batches);
      setLoadError(null);
    } catch (e: any) {
      setLoadError(e.message || 'Failed to load batch reports');
    } finally {
      setLoading(false);
    }
  }, [clientId, token, query]);

  useEffect(() => {
    const debounce = setTimeout(load, 250);
    const poll = setInterval(load, POLL_MS);
    return () => { clearTimeout(debounce); clearInterval(poll); };
  }, [load]);

  // Keeps the running time on each batch counting.
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const batchTemplates = templates.filter((t) => t.kind === 'batch');
  const processTemplates = templates.filter((t) => t.kind === 'process');
  const canSign = SIGNER_ROLES.includes(userRole);
  const showTabs = processTemplates.length > 0 && (batchTemplates.length > 0 || batches.length > 0);
  const showProcess = processTemplates.length > 0 && (tab === 'process' || !showTabs);

  const openStart = (t: BatchTemplate) => {
    setStartForm({ batchNumber: '', product: t.product || '', fields: {} });
    setStarting(t);
  };
  const submitStart = async () => {
    if (!starting) return;
    const missing = (starting.fields || []).find((f) => f.required && !startForm.fields[f.label]?.trim());
    if (missing) { toast.error(`${missing.label} is required`); return; }
    setBusy(true);
    try {
      const b = await batchApi.start(token, clientId, { templateId: starting.id, ...startForm });
      toast.success(`Batch ${b.batchNumber} started`);
      setStarting(null);
      await load();
    } catch (e: any) { toast.error(e.message); } finally { setBusy(false); }
  };

  const openStop = (b: Batch, status: 'completed' | 'aborted') => {
    setStopForm({ status, notes: '' });
    setStopping(b);
  };
  const submitStop = async () => {
    if (!stopping) return;
    if (stopForm.status === 'aborted' && !stopForm.notes.trim()) { toast.error('Enter the reason for aborting the batch'); return; }
    setBusy(true);
    try {
      await batchApi.stop(token, clientId, stopping.id, stopForm);
      toast.success(`Batch ${stopping.batchNumber} ${stopForm.status}`);
      setStopping(null);
      await load();
    } catch (e: any) { toast.error(e.message); } finally { setBusy(false); }
  };

  const openEdit = (b: Batch) => {
    setEditForm({ product: b.product || '', fields: { ...b.fields } });
    setEditing(b);
  };
  const submitEdit = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await batchApi.updateDetails(token, clientId, editing.id, editForm);
      toast.success('Batch details saved');
      setEditing(null);
      await load();
    } catch (e: any) { toast.error(e.message); } finally { setBusy(false); }
  };

  const openSign = (b: Batch) => {
    setSignForm({ meaning: b.signatures.length ? 'Approval' : 'Review', reason: '', password: '' });
    setSigning(b);
  };
  const submitSign = async () => {
    if (!signing) return;
    setBusy(true);
    try {
      await batchApi.sign(token, clientId, signing.id, signForm);
      toast.success(`Batch ${signing.batchNumber} signed`);
      setSigning(null);
      await load();
    } catch (e: any) { toast.error(e.message); } finally { setBusy(false); }
  };

  const run = async (action: () => Promise<unknown>) => {
    try { await action(); } catch (e: any) { toast.error(e.message || 'Download failed'); }
  };

  const downloadProcess = (t: BatchTemplate, format: 'pdf' | 'xlsx') => {
    const from = new Date(range.from);
    const to = new Date(range.to);
    if (!(from < to)) { toast.error('The start of the range must be before the end'); return; }
    run(() => batchApi.downloadProcess(token, clientId, t, format, from, to));
  };
  const quickRange = (hours: number) => setRange({ from: toLocalInput(new Date(Date.now() - hours * 3600 * 1000)), to: toLocalInput(new Date()) });

  if (loading) {
    return <div className="flex h-64 items-center justify-center text-muted-foreground"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading batch reports…</div>;
  }
  if (loadError && templates.length === 0) {
    return (
      <Card className="border-amber-500/30 bg-amber-500/5">
        <CardContent className="p-6">
          <p className="font-semibold text-amber-500">Batch reports could not be loaded</p>
          <p className="text-sm text-muted-foreground">{loadError}</p>
        </CardContent>
      </Card>
    );
  }
  if (templates.length === 0 && batches.length === 0) {
    return (
      <Card className="p-12 text-center">
        <ClipboardList className="mx-auto mb-4 h-14 w-14 text-muted-foreground opacity-50" />
        <h3 className="mb-2 text-xl font-semibold">No reports set up yet</h3>
        <p className="text-muted-foreground">Your administrator creates your batch and process reports in the Admin Panel → Batch Reports.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-2xl font-bold"><ClipboardList className="h-6 w-6" /> Batch Reports</h2>
          <p className="text-sm text-muted-foreground">Run batches and download their reports as PDF or Excel.</p>
        </div>
        {showTabs && (
          <div className="flex rounded-lg border p-0.5" role="group" aria-label="Report type">
            {([['batches', 'Batches'], ['process', 'Process reports']] as const).map(([id, label]) => (
              <button key={id} type="button" aria-pressed={tab === id} onClick={() => setTab(id)}
                className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${tab === id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {showProcess ? (
        <Card>
          <CardHeader className="px-5 pb-3 pt-5"><CardTitle className="text-lg">Process reports</CardTitle></CardHeader>
          <CardContent className="space-y-4 px-5 pb-5">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_auto]">
              <div className="space-y-1.5"><Label htmlFor="batch-from">From</Label><Input id="batch-from" type="datetime-local" className="dark:[color-scheme:dark]" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} /></div>
              <div className="space-y-1.5"><Label htmlFor="batch-to">To</Label><Input id="batch-to" type="datetime-local" className="dark:[color-scheme:dark]" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} /></div>
              <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-1">
                {([['8 h', 8], ['24 h', 24], ['7 days', 168]] as const).map(([label, hours]) => (
                  <Button key={label} type="button" variant="outline" size="sm" onClick={() => quickRange(hours)}>{label}</Button>
                ))}
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {processTemplates.map((t) => (
                <div key={t.id} className="flex flex-col gap-3 rounded-lg border p-4">
                  <div className="min-w-0">
                    <div className="break-words font-semibold">{t.name}</div>
                    {t.description && <p className="mt-0.5 break-words text-sm text-muted-foreground">{t.description}</p>}
                    <p className="mt-1 text-xs text-muted-foreground">{t.tags.length} value{t.tags.length === 1 ? '' : 's'} · up to 31 days · times in {t.timeZone}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => downloadProcess(t, 'pdf')}><FileText className="mr-1.5 h-4 w-4" /> PDF</Button>
                    <Button size="sm" variant="outline" onClick={() => downloadProcess(t, 'xlsx')}><FileSpreadsheet className="mr-1.5 h-4 w-4" /> Excel</Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {batchTemplates.length > 0 && (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {batchTemplates.map((t) => {
                const active = running.find((b) => b.templateId === t.id);
                return (
                  <Card key={t.id} className={active ? 'border-amber-500/50' : ''}>
                    <CardContent className="flex h-full flex-col gap-3 p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="break-words font-semibold">{t.name}</div>
                          {t.description && <p className="mt-0.5 break-words text-sm text-muted-foreground">{t.description}</p>}
                        </div>
                        {active && <StatusBadge status="running" />}
                      </div>
                      {active ? (
                        <>
                          <div>
                            <div className="break-all font-mono text-sm font-bold">{active.batchNumber}</div>
                            <div className="mt-1 text-3xl font-bold tabular-nums">{formatDuration(Date.now() - new Date(active.startedAt).getTime())}</div>
                            <div className="mt-1 break-words text-xs text-muted-foreground">
                              {active.product ? `${active.product} · ` : ''}{active.startSource === 'trigger' ? 'Started by signal' : `Started by ${active.startedByName || 'operator'}`}
                            </div>
                          </div>
                          <div className="mt-auto flex flex-wrap gap-2">
                            <Button size="sm" onClick={() => openStop(active, 'completed')}><Square className="mr-1.5 h-4 w-4" /> Complete</Button>
                            <Button size="sm" variant="outline" onClick={() => openEdit(active)}><Pencil className="mr-1.5 h-4 w-4" /> Details</Button>
                            <Button size="sm" variant="outline" onClick={() => run(() => batchApi.downloadBatch(token, clientId, active, 'pdf'))}><Download className="mr-1.5 h-4 w-4" /> Report so far</Button>
                            <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-600 dark:text-red-400" onClick={() => openStop(active, 'aborted')}><XCircle className="mr-1.5 h-4 w-4" /> Abort</Button>
                          </div>
                        </>
                      ) : (
                        <div className="mt-auto space-y-2">
                          {t.startMode !== 'manual' && t.trigger && (
                            <p className="flex items-start gap-1.5 break-words text-xs text-muted-foreground">
                              <Radio className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Starts by itself when {t.trigger.deviceName} - {t.trigger.tagName} turns on
                            </p>
                          )}
                          {t.startMode !== 'trigger' && (
                            <Button size="sm" onClick={() => openStart(t)}><Play className="mr-1.5 h-4 w-4" /> Start batch</Button>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          <Card>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0 px-5 pb-3 pt-5">
              <CardTitle className="text-lg">Batch history</CardTitle>
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input className="pl-8" placeholder="Search batch or product" aria-label="Search batches" value={query} onChange={(e) => setQuery(e.target.value)} />
              </div>
            </CardHeader>
            <CardContent className="space-y-3 px-5 pb-5">
              {batches.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">{query ? 'No batches match your search.' : 'No batches yet.'}</p>}
              {batches.map((b) => (
                <div key={b.id} className="rounded-lg border p-3">
                  <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="break-all font-mono text-sm font-bold">{b.batchNumber}</span>
                        <StatusBadge status={b.status} />
                        {b.signatures.length > 0 && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                            <ShieldCheck className="h-3.5 w-3.5" /> Signed
                          </span>
                        )}
                      </div>
                      <div className="mt-1 break-words text-sm text-muted-foreground">{b.templateName}{b.product ? ` · ${b.product}` : ''}</div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {formatTime(b.startedAt)}{b.endedAt ? ` · ${formatDuration(new Date(b.endedAt).getTime() - new Date(b.startedAt).getTime())}` : ''}
                      </div>
                    </div>
                    {b.status !== 'running' && (
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => run(() => batchApi.downloadBatch(token, clientId, b, 'pdf'))}><FileText className="mr-1.5 h-4 w-4" /> PDF</Button>
                        <Button size="sm" variant="outline" onClick={() => run(() => batchApi.downloadBatch(token, clientId, b, 'xlsx'))}><FileSpreadsheet className="mr-1.5 h-4 w-4" /> Excel</Button>
                        {b.signatures.length === 0 && <Button size="sm" variant="outline" onClick={() => openEdit(b)}><Pencil className="mr-1.5 h-4 w-4" /> Details</Button>}
                        {canSign && <Button size="sm" onClick={() => openSign(b)}><ShieldCheck className="mr-1.5 h-4 w-4" /> Sign</Button>}
                      </div>
                    )}
                  </div>
                  {b.signatures.length > 0 && (
                    <div className="mt-2 space-y-1 border-t pt-2">
                      {b.signatures.map((s) => (
                        <div key={s.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                          <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
                          <span className="min-w-0 break-words"><b className="text-foreground">{s.meaning}</b> by {s.signedByName || s.signedByEmail} · {formatTime(s.signedAt)}</span>
                          <button type="button" className="font-semibold text-primary hover:underline" onClick={() => run(() => batchApi.downloadSigned(token, clientId, b, s))}>Signed copy</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={!!starting} onOpenChange={(o) => !o && !busy && setStarting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start batch: {starting?.name}</DialogTitle>
            <DialogDescription>Recording starts as soon as you press Start.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Batch number</Label>
              <Input value={startForm.batchNumber} maxLength={60} placeholder={`Leave empty for an automatic number (${starting?.numberPrefix || 'BATCH'}-…)`} onChange={(e) => setStartForm({ ...startForm, batchNumber: e.target.value })} />
            </div>
            <div className="space-y-1.5"><Label>Product</Label><Input value={startForm.product} maxLength={120} onChange={(e) => setStartForm({ ...startForm, product: e.target.value })} /></div>
            <FieldInputs fields={starting?.fields || []} values={startForm.fields} onChange={(fields) => setStartForm({ ...startForm, fields })} />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setStarting(null)} disabled={busy}>Cancel</Button>
            <Button onClick={submitStart} disabled={busy}>{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />} Start</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!stopping} onOpenChange={(o) => !o && !busy && setStopping(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{stopForm.status === 'aborted' ? 'Abort' : 'Complete'} batch {stopping?.batchNumber}?</DialogTitle>
            <DialogDescription>Recording stops and the batch record is locked. This cannot be undone.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>{stopForm.status === 'aborted' ? 'Reason for aborting *' : 'Notes (optional)'}</Label>
            <Input value={stopForm.notes} maxLength={1000} onChange={(e) => setStopForm({ ...stopForm, notes: e.target.value })} />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setStopping(null)} disabled={busy}>Keep running</Button>
            <Button variant={stopForm.status === 'aborted' ? 'destructive' : 'default'} onClick={submitStop} disabled={busy}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{stopForm.status === 'aborted' ? 'Abort batch' : 'Complete batch'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && !busy && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Details of batch {editing?.batchNumber}</DialogTitle>
            <DialogDescription>These can be changed until the batch is signed. Every change is recorded in the audit log.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Product</Label><Input value={editForm.product} maxLength={120} onChange={(e) => setEditForm({ ...editForm, product: e.target.value })} /></div>
            <FieldInputs fields={editing?.templateFields || []} values={editForm.fields} onChange={(fields) => setEditForm({ ...editForm, fields })} />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEditing(null)} disabled={busy}>Cancel</Button>
            <Button onClick={submitEdit} disabled={busy}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!signing} onOpenChange={(o) => !o && !busy && setSigning(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sign batch {signing?.batchNumber}</DialogTitle>
            <DialogDescription>Your name, the time and your reason are added to the report permanently. A signature cannot be removed.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="batch-sign-meaning">Meaning</Label>
              <select id="batch-sign-meaning" className={selectCls} value={signForm.meaning} onChange={(e) => setSignForm({ ...signForm, meaning: e.target.value })}>
                {SIGNATURE_MEANINGS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="space-y-1.5"><Label htmlFor="batch-sign-reason">Reason *</Label><Input id="batch-sign-reason" value={signForm.reason} maxLength={500} onChange={(e) => setSignForm({ ...signForm, reason: e.target.value })} /></div>
            <div className="space-y-1.5"><Label htmlFor="batch-sign-password">Your password *</Label><Input id="batch-sign-password" type="password" autoComplete="current-password" value={signForm.password} onChange={(e) => setSignForm({ ...signForm, password: e.target.value })} /></div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setSigning(null)} disabled={busy}>Cancel</Button>
            <Button onClick={submitSign} disabled={busy || !signForm.reason.trim() || !signForm.password}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />} Sign
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
